import useSWR from "swr";
import { fetcher } from "@/lib/api";
import { PageHeader, Section } from "@/components/Heading";
import { formatPct } from "@/lib/format";
import { useState } from "react";
import { Info, AlertTriangle } from "lucide-react";
import { usePeriod } from "@/components/PeriodFilterBar";

const LABEL = { DAY: "Today", WEEK: "Last 7 days", MTD: "Month to date",
                QTD: "Quarter to date", YTD: "Year to date", LTM: "Last 12 months" };

function abscol(pct) {
  if (pct >= 15) return "bg-destructive/30 text-destructive border-destructive/40";
  if (pct >= 8) return "bg-warning/20 text-warning border-warning/30";
  return "bg-positive/15 text-positive border-positive/30";
}

export default function HrAttendance() {
  const { period } = usePeriod();
  const { data } = useSWR(`/hr/attendance?period=${period}`, fetcher);
  const [explainHeatmap, setExplainHeatmap] = useState(false);
  const [explainDecay, setExplainDecay] = useState(false);
  if (!data) return null;

  // Derived narrative
  const dow = data.day_of_week || [];
  const worstDay = [...dow].sort((a, b) => b.absent_pct - a.absent_pct)[0];
  const bestDay = [...dow].sort((a, b) => a.absent_pct - b.absent_pct)[0];
  const totalEmployees = data.employees?.length || 0;
  const chronicCount = data.chronic_absentees?.length || 0;
  const decayCount = data.attendance_decay?.length || 0;

  return (
    <div>
      <PageHeader
        eyebrow={`People · Attendance · ${LABEL[period] || period}`}
        title="Who showed up, who didn't."
        lede="Day-of-week pattern, decay alerts and the chronic absentees that need a conversation."
      />

      {/* Narrative strip */}
      <div className="grid md:grid-cols-3 gap-px bg-border mb-10">
        <div className="bg-background p-5">
          <div className="label-caps text-destructive mb-2">Worst day</div>
          <div className="font-display text-xl">{worstDay?.day} · {formatPct(worstDay?.absent_pct)}</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Largest absenteeism concentration. Re-rotate weekly offs into this slot to free up weekend headcount.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps text-positive mb-2">Best day</div>
          <div className="font-display text-xl">{bestDay?.day} · {formatPct(bestDay?.absent_pct)}</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Coverage is strongest here. Stack training, audits and stock-take on this day.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps text-warning mb-2">Conversations to have</div>
          <div className="font-display text-xl">{chronicCount} chronic · {decayCount} decaying</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            {totalEmployees} people tracked in window. {chronicCount} below 60%, {decayCount} drifting downward from baseline.
          </p>
        </div>
      </div>

      <Section
        eyebrow="Day-of-week heatmap"
        title="Where the floor leaks"
        right={
          <button onClick={() => setExplainHeatmap(!explainHeatmap)} className="hairline px-3 h-8 text-xs flex items-center gap-2">
            <Info className="w-3.5 h-3.5" /> How it's computed
          </button>
        }
      >
        {explainHeatmap && (
          <div className="hairline p-4 mb-4 bg-secondary/40 text-sm" data-testid="attendance-info-banner">
            For each weekday, the % of scheduled work-days where employees were marked Absent. Weekly offs and approved leaves are NOT counted. Use this to plan coverage rotation.
          </div>
        )}
        <div className="grid grid-cols-7 gap-px bg-border">
          {(data.day_of_week || []).map((d) => (
            <div key={d.day} className={`bg-background p-2 md:p-5 text-center hairline ${abscol(d.absent_pct)}`} data-testid={`heatmap-${d.day}`}>
              <div className="label-caps text-[9px] md:text-[10px]">{d.day}</div>
              <div className="font-mono text-base md:text-2xl mt-1 md:mt-2">{d.absent_pct}%</div>
              <div className="label-caps mt-1 text-[8px] md:text-[10px]">absent</div>
            </div>
          ))}
        </div>
      </Section>

      <Section
        eyebrow="Decay alert"
        title="Drifting downward"
        right={
          <button onClick={() => setExplainDecay(!explainDecay)} className="hairline px-3 h-8 text-xs flex items-center gap-2">
            <Info className="w-3.5 h-3.5" /> Formula
          </button>
        }
      >
        {explainDecay && (
          <div className="hairline p-4 mb-4 bg-secondary/40 text-sm" data-testid="decay-info-banner">
            Employees whose current month attendance dropped more than 10 percentage points vs their personal 3-month baseline. Resigned employees (working_status = IN-ACTIVE) are excluded — they show under Attrition.
          </div>
        )}
        <div className="hidden md:block hairline">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th className="text-right">Present</th>
                <th className="text-right">Absent</th>
                <th className="text-right">Attendance %</th>
              </tr>
            </thead>
            <tbody>
              {(data.attendance_decay || []).map((e) => (
                <tr key={e.emp_id}>
                  <td className="font-display text-sm">{e.name}</td>
                  <td className="text-muted-foreground">{e.department}</td>
                  <td className="text-right">{e.present_days}</td>
                  <td className="text-right text-warning">{e.absent_days}</td>
                  <td className="text-right font-mono">{formatPct(e.attendance_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-2">
          {(data.attendance_decay || []).map((e) => (
            <div key={e.emp_id} className="hairline p-3">
              <div className="font-display text-sm">{e.name}</div>
              <div className="text-[11px] text-muted-foreground">{e.department}</div>
              <div className="font-mono text-xs mt-2 flex gap-4"><span className="text-positive">{e.present_days}P</span><span className="text-warning">{e.absent_days}A</span><span>{formatPct(e.attendance_pct)}</span></div>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Chronic absentees · <60%" title="Conversations to have">
        <div className="hairline p-4 flex items-start gap-3 mb-3 bg-secondary/30">
          <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
          <div className="text-sm">{(data.chronic_absentees || []).length} employees below 60% attendance. Review by HR within the week.</div>
        </div>
        {(data.chronic_absentees || []).length === 0 ? (
          <div className="hairline p-8 text-center text-sm text-muted-foreground">
            No employees found below the 60% threshold for this period. The floor is holding up well.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-px bg-border">
            {(data.chronic_absentees || []).map((e) => (
              <div key={e.emp_id} className="bg-background p-3 flex justify-between items-center">
                <div>
                  <div className="font-display text-sm">{e.name}</div>
                  <div className="text-[11px] text-muted-foreground">{e.department}</div>
                </div>
                <div className="font-mono text-destructive">{formatPct(e.attendance_pct)}</div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
